import React, { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Users, Wrench, Info, MagnifyingGlass, EnvelopeSimple } from "@phosphor-icons/react";

const TABS = [
  { id: "customer", label: "Customers", icon: Users, blurb: "Posting jobs, finding a trader, reviews & disputes." },
  { id: "trade", label: "Tradespeople", icon: Wrench, blurb: "Subscription, job alerts, getting verified." },
  { id: "general", label: "General", icon: Info, blurb: "Payments, data privacy, support." },
];

export default function FAQ() {
  const [groups, setGroups] = useState({ customer: [], trade: [], general: [] });
  const [tab, setTab] = useState("customer");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "FAQ · MyHandyHub";
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/faqs");
        if (alive) setGroups(data.groups || { customer: [], trade: [], general: [] });
      } catch (e) {
        console.error("faqs load", formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const list = groups[tab] || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }, [groups, tab, query]);

  const totalShown = filtered.length;
  const totalForTab = (groups[tab] || []).length;

  // Build JSON-LD FAQPage schema from ALL published FAQs (Google indexes everything)
  const allFaqs = [...(groups.customer || []), ...(groups.trade || []), ...(groups.general || [])];
  useEffect(() => {
    if (allFaqs.length === 0) return;
    const ldJson = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allFaqs.map((f) => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": f.answer,
        },
      })),
    };
    const scriptId = "faq-jsonld";
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.id = scriptId;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ldJson);
    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [allFaqs.length]);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12 sm:py-16" data-testid="faq-page">
        {/* Hero */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: "rgba(226,109,41,0.12)", color: "var(--brand-orange)" }}>
            <Info size={12} weight="fill" /> Help centre
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-4" style={{ color: "var(--brand-green)" }}>
            Frequently asked questions
          </h1>
          <p className="text-base text-[var(--text-muted)] mt-3 max-w-2xl mx-auto">
            Everything you need to know about posting jobs, joining as a trader, and how MyHandyHub keeps things fair for everyone.
          </p>
        </div>

        {/* Search */}
        <div className="mt-8 relative max-w-xl mx-auto">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search e.g. 'verified', 'subscription', 'dispute'…"
            className="brand-input w-full pl-11"
            data-testid="faq-search"
          />
        </div>

        {/* Tabs */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="faq-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const count = (groups[t.id] || []).length;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`faq-tab-${t.id}`}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  active
                    ? "border-[var(--brand-orange)] bg-white shadow-sm"
                    : "border-[var(--border-stone)] bg-white/60 hover:bg-white hover:border-[var(--brand-green)]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <Icon size={22} weight={active ? "duotone" : "regular"} style={{ color: active ? "var(--brand-orange)" : "var(--brand-green)" }} />
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FAF8F2] text-[var(--text-muted)]">{count}</span>
                </div>
                <div className="font-display font-extrabold text-base mt-2" style={{ color: "var(--brand-green)" }}>{t.label}</div>
                <div className="text-xs text-[var(--text-muted)] mt-0.5">{t.blurb}</div>
              </button>
            );
          })}
        </div>

        {/* Questions */}
        <div className="mt-8 brand-card p-2 sm:p-4">
          {loading ? (
            <p className="text-[var(--text-muted)] text-center py-10">Loading…</p>
          ) : totalShown === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--text-muted)]">
                {query ? `No match for "${query}" in this section.` : "No questions in this section yet."}
              </p>
              {query && totalForTab > 0 && (
                <button onClick={() => setQuery("")} className="btn-ghost mt-3 text-sm" data-testid="faq-clear-search">
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full" data-testid={`faq-list-${tab}`}>
              {filtered.map((f) => (
                <AccordionItem key={f.faq_id} value={f.faq_id} className="border-[var(--border-stone)] last:border-b-0">
                  <AccordionTrigger className="px-3 sm:px-4 py-5 font-display font-extrabold text-base sm:text-lg text-left hover:no-underline" style={{ color: "var(--brand-green)" }} data-testid={`faq-q-${f.faq_id}`}>
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-3 sm:px-4 pb-5 text-sm sm:text-base leading-relaxed text-[var(--text-main)]" data-testid={`faq-a-${f.faq_id}`}>
                    <div className="whitespace-pre-wrap">{f.answer}</div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        {/* Still stuck CTA */}
        <div className="mt-10 brand-card p-6 sm:p-8 text-center" style={{ background: "linear-gradient(135deg, rgba(17,82,59,0.04) 0%, rgba(226,109,41,0.04) 100%)" }}>
          <EnvelopeSimple size={32} weight="duotone" className="mx-auto" style={{ color: "var(--brand-orange)" }} />
          <h2 className="font-display font-extrabold text-xl sm:text-2xl mt-3" style={{ color: "var(--brand-green)" }}>
            Still got a question?
          </h2>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Drop us a line — we usually reply within a few hours during UK business hours.
          </p>
          <a
            href="mailto:hello@myhandyhub.co.uk"
            className="btn-primary mt-4 inline-flex"
            data-testid="faq-contact-btn"
          >
            Email hello@myhandyhub.co.uk
          </a>
          <p className="text-xs text-[var(--text-muted)] mt-3">
            New here? <Link to="/how-it-works" className="underline hover:text-[var(--brand-green)]">See how it works</Link>
            {" · "}
            <Link to="/pricing" className="underline hover:text-[var(--brand-green)]">View pricing</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
